<template>
  <v-card outlined class="fill-height d-flex flex-column">
    <template v-if="conversation">
      <v-toolbar flat dense>
        <!-- En pantalla chica el hilo reemplaza a la lista: sin esto no habría
             cómo volver. -->
        <i-btn
          v-if="$vuetify.breakpoint.smAndDown"
          :title="$t('back')"
          icon="fa-arrow-left"
          color="primary"
          text
          class="mr-1"
          @click="$emit('back')"
        />
        <v-toolbar-title class="subtitle-1">
          {{ conversation.customer_name }}
          <div class="caption grey--text">{{ conversation.contact_phone }}</div>
        </v-toolbar-title>
        <v-spacer />
        <i-btn
          v-if="canAssign"
          :title="$t('assign')"
          icon="fa-user-check"
          color="primary"
          text
          :small="!$vuetify.breakpoint.smAndDown"
          @click="assignDialog = true"
        />
        <i-btn
          v-if="canClose && conversation.state !== 'closed'"
          :title="$tc('close', 1)"
          icon="fa-check"
          color="primary"
          text
          :small="!$vuetify.breakpoint.smAndDown"
          @click="close()"
        />
      </v-toolbar>

      <v-divider />

      <div ref="thread" class="thread-scroll flex-grow-1 pa-3">
        <v-skeleton-loader v-if="loading" type="paragraph@3" />
        <div v-else-if="messages.length === 0" class="text-center grey--text">
          {{ $t('no_messages') }}
        </div>
        <div
          v-for="message in messages"
          :key="`message-${message.pk}`"
          class="d-flex mb-2"
          :class="message.direction === 'in' ? '' : 'justify-end'"
        >
          <v-sheet
            rounded
            class="pa-2 message-bubble"
            :color="
              message.direction === 'in' ? 'grey lighten-3' : 'green lighten-4'
            "
          >
            <div
              v-for="file in message.attachments"
              :key="`file-${file.pk}`"
              class="mb-1"
            >
              <!-- Una imagen se muestra; el resto va como enlace -->
              <a
                v-if="isImage(file)"
                :href="fileUrl(file)"
                target="_blank"
                rel="noopener"
              >
                <v-img
                  :src="fileUrl(file)"
                  max-width="220"
                  max-height="220"
                  contain
                  class="rounded"
                />
              </a>
              <a
                v-else
                :href="fileUrl(file)"
                target="_blank"
                rel="noopener"
                class="caption d-flex align-center"
              >
                <v-icon small class="mr-1">fa-paperclip</v-icon>
                {{ file.original_name }}
                <span v-if="file.size" class="ml-1 grey--text">
                  ({{ humanSize(file.size) }})
                </span>
              </a>
            </div>
            <div class="body-2 message-body">{{ message.body }}</div>
            <div class="caption grey--text text--darken-1 text-right">
              <span v-if="message.user_name">{{ message.user_name }} · </span>
              {{ time(message.timestamp) }}
              <v-icon
                v-if="message.direction === 'out'"
                x-small
                :color="message.status === 'failed' ? 'red' : 'grey'"
              >
                {{ statusIcon(message.status) }}
              </v-icon>
            </div>
            <!-- El motivo tal como lo da la plataforma queda en el title: le
                 sirve a quien tenga que reportarlo, no a quien atiende. -->
            <div
              v-if="message.error_message"
              class="caption red--text"
              :title="message.error_message"
            >
              {{ errorLabel(message) }}
            </div>
          </v-sheet>
        </div>
      </div>

      <v-divider />

      <v-card-text class="py-2">
        <v-alert
          v-if="!conversation.can_send_free_text"
          type="info"
          dense
          text
          class="mb-2 caption"
        >
          {{ $t('window_closed') }}
        </v-alert>
        <v-row no-gutters align="center">
          <v-col cols="1" class="text-center">
            <input ref="file" type="file" class="d-none" @change="uploadFile" />
            <i-btn
              :title="$t('attach_file')"
              icon="fa-paperclip"
              color="primary"
              text
              :loading="uploading"
              :disabled="!canAttach || !conversation.can_send_free_text"
              @click="$refs.file.click()"
            />
          </v-col>
          <v-col cols="9">
            <v-textarea
              v-model="body"
              :label="$t('write_message')"
              :disabled="!canReply || !conversation.can_send_free_text"
              rows="1"
              auto-grow
              outlined
              dense
              hide-details
              @keydown.enter.exact.prevent="send()"
            />
          </v-col>
          <v-col cols="2" class="text-center">
            <i-btn
              :title="$tc('send', 0)"
              icon="fa-paper-plane"
              color="primary"
              :loading="sending"
              :disabled="!canReply || !conversation.can_send_free_text"
              @click="send()"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <i-modal
        :title="$t('assign')"
        :value="assignDialog"
        :load="assigning"
        @submit="assign()"
        @close="assignDialog = false"
      >
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="assignee"
              :items="users"
              item-text="name"
              item-value="id"
              :label="$tc('user', 0)"
              outlined
              dense
              clearable
            />
          </v-col>
        </v-row>
      </i-modal>
    </template>
    <div
      v-else
      class="d-flex align-center justify-center fill-height grey--text"
    >
      {{ $t('select_conversation') }}
    </div>
  </v-card>
</template>
<script>
import { mapGetters } from 'vuex'
export default {
  name: 'message-thread',
  data() {
    return {
      body: '',
      sending: false,
      loading: false,
      assignDialog: false,
      assigning: false,
      assignee: null,
      uploading: false,
      users: [],
      // Urls de objeto de los adjuntos ya descargados, por identificador
      fileUrls: {}
    }
  },
  computed: {
    ...mapGetters({
      conversation: 'whatsapp/getCurrent',
      messages: 'whatsapp/getMessages',
      company: 'company/getCompanyData',
      lastEvent: 'whatsapp/getLastEvent',
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin'
    }),
    canReply() {
      return (
        this.isAdmin || this.getPermissions(['whatsapp.reply_conversation'])
      )
    },
    canAssign() {
      return (
        this.isAdmin || this.getPermissions(['whatsapp.assign_conversation'])
      )
    },
    canClose() {
      return (
        this.isAdmin || this.getPermissions(['whatsapp.close_conversation'])
      )
    },
    canAttach() {
      return this.isAdmin || this.getPermissions(['whatsapp.send_attachment'])
    }
  },
  methods: {
    time(value) {
      return value ? this.$moment(value).format('DD/MM HH:mm') : ''
    },
    /**
     * Traduce el motivo del fallo a algo accionable.
     *
     * La plataforma devuelve textos en inglés como 'Authentication Error' o
     * '(#131030) Recipient phone number not in allowed list'. Quien atiende no
     * puede hacer nada con eso. Se traduce lo que sabemos reconocer y el resto
     * se muestra como vino, antes que esconder el motivo.
     */
    errorLabel(message) {
      const numero = (message.error_message || '').match(/\(#(\d+)\)/)
      const clave = numero ? `whatsapp_error_${numero[1]}` : null

      if (clave && this.$te(clave)) {
        return this.$t(clave)
      }
      if (String(message.error_code) === '401') {
        return this.$t('whatsapp_error_auth')
      }
      return message.error_message
    },
    statusIcon(status) {
      if (status === 'failed') {
        return 'fa-exclamation-circle'
      }
      if (status === 'read' || status === 'delivered') {
        return 'fa-check-double'
      }
      if (status === 'sent') {
        return 'fa-check'
      }
      return 'fa-clock'
    },
    isImage(file) {
      return (file.mime_type || '').startsWith('image/')
    },
    /**
     * Los adjuntos no tienen una dirección propia: la carpeta de medios no
     * pide sesión y ahí viajan cotizaciones y artes de clientes. Se piden
     * autenticados y se muestran desde una url de objeto, que vive sólo en
     * esta pestaña y no se puede compartir.
     */
    fileUrl(file) {
      return this.fileUrls[file.pk] || ''
    },
    async loadAttachments() {
      const pendientes = []
      this.messages.forEach((message) => {
        ;(message.attachments || []).forEach((file) => {
          if (!this.fileUrls[file.pk]) {
            pendientes.push(file)
          }
        })
      })

      for (const file of pendientes) {
        try {
          const response = await this.$api.whatsapp.attachment.download({
            pk: file.pk,
            opt: {
              params: { company_id: this.company.pk },
              skipErrorToast: true
            }
          })
          this.$set(this.fileUrls, file.pk, URL.createObjectURL(response.data))
        } catch (e) {
          // Un adjunto que no baja no puede romper el historial completo
        }
      }
    },
    releaseAttachments() {
      Object.values(this.fileUrls).forEach((url) => URL.revokeObjectURL(url))
      this.fileUrls = {}
    },
    humanSize(bytes) {
      if (bytes < 1024) {
        return `${bytes} B`
      }
      if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`
      }
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    async uploadFile(event) {
      const file = event.target.files ? event.target.files[0] : null
      // El input se limpia siempre para poder volver a elegir el mismo archivo
      event.target.value = ''

      if (!file || this.uploading) {
        return
      }

      const form = new FormData()
      form.append('file', file)
      if (this.body) {
        form.append('body', this.body)
      }

      this.uploading = true
      try {
        const response = await this.$api.whatsapp.sendFile({
          pk: this.conversation.pk,
          form,
          opt: { params: { company_id: this.company.pk } }
        })
        this.$store.dispatch('whatsapp/addMessage', response.data)
        this.body = ''
        this.scrollDown()
      } finally {
        this.uploading = false
      }
    },
    scrollDown() {
      this.$nextTick(() => {
        const thread = this.$refs.thread
        if (thread) {
          thread.scrollTop = thread.scrollHeight
        }
      })
    },
    async loadMessages() {
      if (!this.conversation) {
        return
      }
      this.loading = true
      try {
        const response = await this.$api.whatsapp.messages({
          pk: this.conversation.pk,
          opt: { params: { company_id: this.company.pk } }
        })
        this.$store.dispatch('whatsapp/setMessages', response.data)
        this.scrollDown()
        this.loadAttachments()
      } finally {
        this.loading = false
      }
    },
    async send() {
      const text = (this.body || '').trim()
      if (!text || this.sending) {
        return
      }
      this.sending = true
      try {
        const response = await this.$api.whatsapp.reply({
          pk: this.conversation.pk,
          form: { body: text },
          opt: { params: { company_id: this.company.pk } }
        })
        this.$store.dispatch('whatsapp/addMessage', response.data)
        this.body = ''
        this.scrollDown()
      } finally {
        this.sending = false
      }
    },
    async assign() {
      this.assigning = true
      try {
        const response = await this.$api.whatsapp.assign({
          pk: this.conversation.pk,
          form: { user: this.assignee },
          opt: { params: { company_id: this.company.pk } }
        })
        this.$store.dispatch('whatsapp/setCurrent', response.data)
        this.assignDialog = false
        this.$emit('updated')
      } finally {
        this.assigning = false
      }
    },
    async close() {
      const response = await this.$api.whatsapp.close({
        pk: this.conversation.pk,
        opt: { params: { company_id: this.company.pk } }
      })
      this.$store.dispatch('whatsapp/setCurrent', response.data)
      this.$emit('updated')
    },
    /**
     * Carga a quiénes se les puede pasar la conversación.
     *
     * No es la lista de usuarios de la empresa: ahí también están sus clientes
     * y proveedores, que no atienden la bandeja.
     */
    async loadUsers() {
      const response = await this.$api.whatsapp.agents({
        opt: { params: { company_id: this.company.pk } }
      })
      this.users =
        response.data.results !== undefined
          ? response.data.results
          : response.data
    }
  },
  watch: {
    // El servidor avisa por el socket: si el evento es de la conversación
    // abierta, se recarga el historial.
    lastEvent(event) {
      if (
        event &&
        this.conversation &&
        event.conversation === this.conversation.pk
      ) {
        this.loadMessages()
      }
    },
    conversation: {
      immediate: true,
      handler(value) {
        this.body = ''
        // Los adjuntos de la conversacion anterior ya no se van a mostrar
        this.releaseAttachments()
        if (value) {
          this.assignee = value.assigned_user
          this.loadMessages()
        }
      }
    }
  },
  mounted() {
    this.loadUsers()
  },
  beforeDestroy() {
    this.releaseAttachments()
  }
}
</script>
<style scoped>
/* Vuetify trae `flex: 1 1 auto` en `.v-toolbar` y `flex: 1 1 0` en
   `.v-divider`. Dentro de una tarjeta en columna eso los estira: el encabezado
   se comía casi la mitad del alto y el primer mensaje aparecía a media
   pantalla, con las líneas flotando sueltas. */
.v-card > .v-toolbar,
.v-card > .v-divider {
  flex: none;
  margin: 0;
}
/* `min-height: 0` deja que el hilo se achique por debajo de su contenido y
   scrollee por dentro. El tope por alto de ventana sólo hace falta en pantalla
   chica, que es donde la fila no lleva alto fijo. */
.thread-scroll {
  overflow-y: auto;
  min-height: 0;
}
@media (max-width: 959px) {
  .thread-scroll {
    max-height: calc(100vh - 380px);
  }
}
.message-bubble {
  max-width: 75%;
}
.message-body {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
